'use client';

// Bloc widget "Fonctionnalités" pour les pages CMS.
// Charge la liste GLOBALE des fonctionnalités (settings.features) et affiche
// uniquement celles dont l'ID est dans config.feature_ids (vide = toutes).
//
// IMPORTANT — anti-flash : reçoit `settings` en prop depuis BlockRenderer →
// SSR HTML contient deja les bonnes features.
//
// V1.25.938 — Suppression TOTALE du refetch client. Cause racine du bug
// "1 clic sur 2" : meme avec JSON.stringify guard, le moindre changement
// declenchait un setState → re-render → demontage/remontage des <a> de la
// grille → si user cliquait, clic perdu. Maintenant : 100% SSR → clics fiables.
import { FeatureCard } from '@/components/Features';
import { applyTextStyle } from '@/lib/textStyle';

export default function FeaturesGridBlock({ config, settings: settingsProp }) {
  const allFeatures = Array.isArray(settingsProp?.features) ? settingsProp.features : [];

  // ── Identifiant stable d'une feature ──────────────────────────────
  // Priorite a f.id (UUID backend), fallback sur slug du titre (`t:<title>`).
  // Le SAME helper est utilise dans FeaturesGridEditor.jsx — les deux DOIVENT
  // matcher exactement, sinon : cases cochees mais aucune feature affichee.
  const featureKey = (f) => {
    if (f && typeof f.id === 'string' && f.id) return f.id;
    if (f && f.title) return 't:' + String(f.title).trim().toLowerCase();
    return null;
  };

  // Nettoyage defensif : feature_ids peut contenir des null/undefined herites
  // de l'ancien bug (avant le fix, toggleFeature(undefined) sauvegardait
  // `[undefined]` qui devient `[null]` en JSON). On filtre Boolean pour ne
  // garder que les vraies clés.
  const rawIds = Array.isArray(config?.feature_ids) ? config.feature_ids : [];
  const ids = rawIds.filter(Boolean);

  // Si feature_ids contient UNIQUEMENT des entrees nulles (corrupt) → on
  // affiche toutes les features comme si aucune n'etait selectionnee. Sinon
  // l'utilisateur voit une page vide alors qu'il pense avoir tout selectionne.
  const allCorrupt = rawIds.length > 0 && ids.length === 0;

  // Filtre normal si on a des ids valides, sinon on affiche tout
  const visible = (ids.length > 0 && !allCorrupt)
    ? allFeatures.filter(f => ids.includes(featureKey(f)))
    : allFeatures;

  // Si l'utilisateur attend des features mais aucune ne match (par exemple
  // les ids saved sont obsoletes apres une migration de catalogue), on
  // affiche aussi tout au lieu d'une page vide silencieuse.
  const finalVisible = (ids.length > 0 && visible.length === 0)
    ? allFeatures
    : visible;

  if (!finalVisible || finalVisible.length === 0) return null;

  const cols = Math.max(2, Math.min(4, parseInt(config?.columns, 10) || 3));
  const minW = cols === 2 ? 360 : cols === 3 ? 280 : 220;

  return (
    <section style={{ padding: '44px 0', background: '#fff' }}>
      <div className="container-page">
        {(config?.title || config?.subtitle) && (
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            {config?.title && (
              <h2 className="section-title" style={applyTextStyle(config.title_style, { textAlign: 'center' })}>
                {config.title}
              </h2>
            )}
            {config?.subtitle && (
              <p className="section-subtitle" style={applyTextStyle(config.subtitle_style)}>{config.subtitle}</p>
            )}
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${minW}px, 1fr))`,
          gap: 24,
        }}>
          {visible.map((f, idx) => (
            <FeatureCard key={f.id || idx} feature={f} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
